import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import authService from '../../services/authService';
import { showError, showSuccess } from '../../utils/toast';

const SignUpPage = () => {
    const navigate = useNavigate();
    const { login, user, isAuthenticated } = useAuth();
    const [formData, setFormData] = useState({
        full_name: '',
        student_id: '',
        enrollment_year: new Date().getFullYear(),
        email: '',
        campus: '',
        department: '',
        password: '',
        confirm_password: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Weak' });
    const [campuses, setCampuses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [filteredDepartments, setFilteredDepartments] = useState([]);

    // Redirect if already logged in
    useEffect(() => {
        if (isAuthenticated()) {
            if (user?.role === 'admin') {
                navigate('/admin/dashboard', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [user, isAuthenticated, navigate]);

    useEffect(() => {
        fetchCampusesAndDepartments();
    }, []);

    useEffect(() => {
        // Filter departments when campus changes
        if (formData.campus) {
            const filtered = departments.filter(dept => dept.campus_id === formData.campus);
            setFilteredDepartments(filtered);
            // Reset department if it doesn't belong to selected campus
            if (formData.department) {
                const deptExists = filtered.find(d => d._id === formData.department);
                if (!deptExists) {
                    setFormData(prev => ({ ...prev, department: '' }));
                }
            }
        } else {
            setFilteredDepartments([]);
        }
    }, [formData.campus, departments]);

    const fetchCampusesAndDepartments = async () => {
        try {
            const [campusesRes, departmentsRes] = await Promise.all([
                api.get('/settings/campuses'),
                api.get('/settings/departments')
            ]);
            if (campusesRes.data.success) {
                setCampuses(campusesRes.data.data);
            }
            if (departmentsRes.data.success) {
                setDepartments(departmentsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const calculatePasswordStrength = (password) => {
        let score = 0;
        if (password.length >= 8) score++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^a-zA-Z\d]/.test(password)) score++;

        const labels = ['Weak', 'Fair', 'Good', 'Strong'];
        return { score, label: labels[Math.min(score, 3)] };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        let processedValue = value;
        
        // Auto-format Student ID to match FA24-BSE-042 pattern
        if (name === 'student_id') {
            // Remove any existing hyphens and convert to uppercase
            processedValue = value.toUpperCase().replace(/-/g, '');
            
            // Add hyphens at correct positions
            if (processedValue.length > 4) {
                processedValue = processedValue.slice(0, 4) + '-' + processedValue.slice(4);
            }
            if (processedValue.length > 8) {
                // Find position after program code (between 2-4 letters after first hyphen)
                const afterFirstHyphen = processedValue.substring(5);
                const programCodeMatch = afterFirstHyphen.match(/^[A-Z]{2,4}/);
                if (programCodeMatch) {
                    const programCodeLength = programCodeMatch[0].length;
                    const insertPosition = 5 + programCodeLength;
                    processedValue = processedValue.slice(0, insertPosition) + '-' + processedValue.slice(insertPosition);
                }
            }
            // Limit to format: XX##-XXX(X)-###
            processedValue = processedValue.slice(0, 15);
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        // Calculate password strength
        if (name === 'password') {
            setPasswordStrength(calculatePasswordStrength(processedValue));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Full name validation
        if (!formData.full_name || formData.full_name.trim().length < 3) {
            newErrors.full_name = 'Full name must be at least 3 characters';
        }

        // Student ID validation - Format: FA24-BSE-109
        if (!formData.student_id) {
            newErrors.student_id = 'Student ID is required';
        } else if (!/^[A-Z]{2}\d{2}-[A-Z]{2,4}-\d{3}$/.test(formData.student_id)) {
            newErrors.student_id = 'Invalid format. Use: FA24-BSE-109';
        }

        // Enrollment year validation
        const currentYear = new Date().getFullYear();
        if (!formData.enrollment_year || formData.enrollment_year < 2000 || formData.enrollment_year > currentYear) {
            newErrors.enrollment_year = 'Invalid enrollment year';
        }

        // Email validation
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Campus validation
        if (!formData.campus) {
            newErrors.campus = 'Please select a campus';
        }

        // Department validation
        if (!formData.department) {
            newErrors.department = 'Please select a department';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one uppercase letter';
        } else if (!/[a-z]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one lowercase letter';
        } else if (!/\d/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one number';
        } else if (!/[!@#$%^&*]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one special character (!@#$%^&*)';
        }

        // Confirm password validation
        if (!formData.confirm_password) {
            newErrors.confirm_password = 'Please confirm your password';
        } else if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('Form submitted, validating...');

        if (!validateForm()) {
            console.log('Validation failed, stopping submission');
            return false;
        }

        setIsLoading(true);

        try {
            // Prepare data for backend (exclude confirm_password)
            const { confirm_password, ...registrationData } = formData;
            
            // Call backend API
            const response = await authService.register(registrationData);

            if (response.success) {
                showSuccess('Registration successful! Logging you in...');

                // Auto-login after registration
                const loginResponse = await authService.login(formData.email, formData.password);

                if (loginResponse.success) {
                    login(
                        loginResponse.user,
                        loginResponse.access_token,
                        loginResponse.refresh_token
                    );
                    navigate('/dashboard');
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
            const errorDetails = error.response?.data?.errors;

            console.log('Registration error:', errorMessage, errorDetails, error);

            if (errorDetails) {
                setErrors(errorDetails);
            } else {
                setErrors({ submit: errorMessage });
            }

            showError(errorMessage);
        } finally {
            setIsLoading(false);
        }

        return false;
    };

    const getPasswordStrengthColor = () => {
        const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-primary'];
        return colors[passwordStrength.score];
    };

    const getPasswordStrengthWidth = () => {
        return `${(passwordStrength.score + 1) * 25}%`;
    };

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
            {/* Top Navigation Bar */}
            <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-primary/10 bg-white dark:bg-background-dark px-10 py-3 w-full">
                <div className="flex items-center gap-4 text-[#131416] dark:text-white">
                    <Link to="/" className="text-gray-500 hover:text-primary transition-colors" title="Back to Home">
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </Link>
                    <Link to="/" className="flex items-center gap-4 group">
                        <div className="h-10 w-auto text-primary group-hover:scale-105 transition-transform">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c0/COMSATS_new_logo.jpg/770px-COMSATS_new_logo.jpg"
                                alt="University Portal"
                                className="h-full w-auto object-contain"
                            />
                        </div>
                        <h2 className="text-[#131416] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] group-hover:text-primary transition-colors">
                            University Portal
                        </h2>
                    </Link>
                </div>
                <div className="flex items-center gap-4">
                    <Link to="/" className="hidden sm:block text-sm font-medium text-gray-500 hover:text-primary transition-colors">
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow flex items-center justify-center p-6">
                <div className="w-full max-w-[540px] bg-white dark:bg-[#1c2126] rounded-xl shadow-xl border border-primary/5 overflow-hidden">
                    <div className="p-8">
                        {/* Headline Section */}
                        <div className="text-center mb-8">
                            <h1 className="text-[#131416] dark:text-white text-[32px] font-bold leading-tight mb-2">
                                Student Sign Up
                            </h1>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                Join your academic community today
                            </p>
                        </div>

                        {errors.submit && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                                {errors.submit}
                            </div>
                        )}

                        {/* Registration Form */}
                        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
                            {/* Full Name */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <input
                                        className={`w-full h-12 rounded-lg border ${errors.full_name ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                            } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                        placeholder="Aqa Noori"
                                        type="text"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.full_name && (
                                    <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Student ID */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                        Student ID
                                    </label>
                                    <input
                                        className={`w-full h-12 rounded-lg border ${errors.student_id ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                            } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                        placeholder="FA24-BSE-109"
                                        type="text"
                                        name="student_id"
                                        value={formData.student_id}
                                        onChange={handleChange}
                                    />
                                    {errors.student_id && (
                                        <p className="text-red-500 text-xs mt-1">{errors.student_id}</p>
                                    )}
                                    <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                                        Format: Session-Program-Number (e.g., FA24-BSE-109)
                                    </p>
                                </div>

                                {/* Enrollment Year */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                        Enrollment Year
                                    </label>
                                    <input
                                        className={`w-full h-12 rounded-lg border ${errors.enrollment_year ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                            } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                        placeholder="2024"
                                        type="number"
                                        name="enrollment_year"
                                        value={formData.enrollment_year}
                                        onChange={handleChange}
                                    />
                                    {errors.enrollment_year && (
                                        <p className="text-red-500 text-xs mt-1">{errors.enrollment_year}</p>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                    Email Address
                                </label>
                                <input
                                    className={`w-full h-12 rounded-lg border ${errors.email ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                        } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                    placeholder="your.email@gmail.com"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                )}
                            </div>

                            {/* Campus Dropdown */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                    Campus
                                </label>
                                <select
                                    className={`w-full h-12 rounded-lg border ${errors.campus ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                        } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none appearance-none cursor-pointer`}
                                    name="campus"
                                    value={formData.campus}
                                    onChange={handleChange}
                                >
                                    <option value="">Select your campus</option>
                                    {campuses.map((campus) => (
                                        <option key={campus._id} value={campus._id}>
                                            {campus.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.campus && (
                                    <p className="text-red-500 text-xs mt-1">{errors.campus}</p>
                                )}
                            </div>

                            {/* Department Dropdown */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                    Department
                                </label>
                                <select
                                    className={`w-full h-12 rounded-lg border ${errors.department ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                        } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none appearance-none cursor-pointer`}
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    disabled={!formData.campus}
                                >
                                    <option value="">
                                        {formData.campus ? 'Select your department' : 'Select campus first'}
                                    </option>
                                    {filteredDepartments.map((dept) => (
                                        <option key={dept._id} value={dept.name}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.department && (
                                    <p className="text-red-500 text-xs mt-1">{errors.department}</p>
                                )}
                            </div>

                            {/* Password Section */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                    Password
                                </label>
                                <input
                                    className={`w-full h-12 rounded-lg border ${errors.password ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                        } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                    placeholder="••••••••"
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                {errors.password && (
                                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                                )}

                                {/* Password Strength Indicator */}
                                {formData.password && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between text-[11px] mb-1">
                                            <span className="text-gray-500 dark:text-gray-400 font-medium">
                                                Password Strength
                                            </span>
                                            <span className={`font-bold ${passwordStrength.score >= 3 ? 'text-primary' :
                                                passwordStrength.score >= 2 ? 'text-yellow-500' : 'text-red-500'
                                                }`}>
                                                {passwordStrength.label}
                                            </span>
                                        </div>
                                        <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden flex gap-1">
                                            <div
                                                className={`h-full ${getPasswordStrengthColor()} rounded-full transition-all duration-300`}
                                                style={{ width: getPasswordStrengthWidth() }}
                                            ></div>
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1 italic">
                                            Use 8+ characters with a mix of letters, numbers & symbols
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password Section */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-semibold">
                                    Confirm Password
                                </label>
                                <input
                                    className={`w-full h-12 rounded-lg border ${errors.confirm_password ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                        } bg-white dark:bg-background-dark text-[#131416] dark:text-white px-4 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                    placeholder="••••••••"
                                    type="password"
                                    name="confirm_password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                />
                                {errors.confirm_password && (
                                    <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>
                                )}
                                {formData.confirm_password && formData.password === formData.confirm_password && (
                                    <p className="text-green-600 dark:text-green-400 text-xs mt-1 flex items-center gap-1">
                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                        Passwords match
                                    </p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                className="w-full h-14 bg-primary hover:bg-primary/90 text-white rounded-lg font-bold text-base shadow-md transition-all active:scale-[0.98] mt-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                type="submit"
                                disabled={isLoading}
                            >
                                <span className="material-symbols-outlined text-xl">how_to_reg</span>
                                {isLoading ? 'Signing Up...' : 'Sign Up'}
                            </button>
                        </form>

                        {/* Footer Link */}
                        <div className="mt-8 text-center">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Already have an account?{' '}
                                <Link to="/login" className="text-primary font-bold hover:underline">
                                    Log in
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer Meta */}
            <footer className="py-6 text-center text-gray-400 text-xs">
                <p>© 2024 University Student Portal. All rights reserved.</p>
                <div className="flex justify-center gap-4 mt-2">
                    <button type="button" onClick={(e) => e.preventDefault()} className="hover:text-primary">Terms of Service</button>
                    <button type="button" onClick={(e) => e.preventDefault()} className="hover:text-primary">Privacy Policy</button>
                </div>
            </footer>
        </div>
    );
};

export default SignUpPage;
